import React, { useState, useEffect } from 'react';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from '../../firebase/config';
import { toast } from 'react-hot-toast';

import LoadingOverlay from '../../components/LoadingOverlay';

const AdminProducts = () => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [newCategory, setNewCategory] = useState({
    name: '',
    brand: ''
  });
  const [savingCategory, setSavingCategory] = useState(false);
  const [deletingCategoryId, setDeletingCategoryId] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    price: '',
    discountedPrice: '',
    sku: '',
    category: '',
    brand: '',
    sizes: [],
    stock: '',
    images: []
  });
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [saving, setSaving] = useState(false);

  const brands = ['House of Deepak', 'Scan by Deepak Raj'];
  const availableSizes = ['XS', 'S', 'M', 'L', 'XL', 'XXL'];

  useEffect(() => {
    fetchCategories();
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const snapshot = await getDocs(collection(db, 'products'));
      const productsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setProducts(productsData);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching products:', error);
      toast.error('Failed to fetch products');
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const snapshot = await getDocs(collection(db, 'categories'));
      const categoriesData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setCategories(categoriesData);
    } catch (error) {
      console.error('Error fetching categories:', error);
      toast.error('Failed to fetch categories');
    }
  };

  const handleAddCategory = async (e) => {
    e.preventDefault();

    if (!newCategory.name.trim() || !newCategory.brand) {
      toast.error('Please fill in all fields');
      return;
    }

    // Check if category already exists for this brand
    const exists = categories.some(
      cat => cat.name.toLowerCase() === newCategory.name.toLowerCase() && cat.brand === newCategory.brand
    );

    if (exists) {
      toast.error('This category already exists for this brand');
      return;
    }

    try {
      setSavingCategory(true);
      await addDoc(collection(db, 'categories'), {
        name: newCategory.name.trim(),
        brand: newCategory.brand,
        createdAt: new Date()
      });

      toast.success('Category added successfully');
      setNewCategory({ name: '', brand: '' });
      setShowCategoryModal(false);
      fetchCategories();
    } catch (error) {
      console.error('Error adding category:', error);
      toast.error('Failed to add category');
    } finally {
      setSavingCategory(false);
    }
  };

  const handleDeleteCategory = async (categoryId, categoryName) => {
    if (!window.confirm(`Are you sure you want to delete "${categoryName}"? This will not affect existing products.`)) {
      return;
    }

    try {
      setDeletingCategoryId(categoryId);
      await deleteDoc(doc(db, 'categories', categoryId));
      toast.success('Category deleted successfully');
      fetchCategories();
    } catch (error) {
      console.error('Error deleting category:', error);
      toast.error('Failed to delete category');
    } finally {
      setDeletingCategoryId(null);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSizeToggle = (size) => {
    setFormData(prev => ({
      ...prev,
      sizes: prev.sizes.includes(size)
        ? prev.sizes.filter(s => s !== size)
        : [...prev.sizes, size]
    }));
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploading(true);
    setUploadProgress(0);
    const uploadedImages = [];

    try {
      let completed = 0;
      for (const file of files) {
        const timestamp = Date.now();
        const fileName = `products/${timestamp}_${file.name}`;
        const storageRef = ref(storage, fileName);

        await uploadBytes(storageRef, file);
        const downloadURL = await getDownloadURL(storageRef);
        uploadedImages.push(downloadURL);
        completed += 1;
        setUploadProgress(Math.round((completed / files.length) * 100));
      }

      setFormData(prev => ({
        ...prev,
        images: [...prev.images, ...uploadedImages]
      }));

      toast.success('Images uploaded successfully');
    } catch (error) {
      console.error('Error uploading images:', error);
      toast.error('Failed to upload images');
    } finally {
      setUploading(false);
      setTimeout(() => setUploadProgress(0), 400);
    }
  };

  const removeImage = async (index) => {
    const imageUrl = formData.images[index];

    try {
      // Extract the file path from the Firebase Storage URL
      // URL format: https://firebasestorage.googleapis.com/v0/b/{bucket}/o/{path}?alt=media&token={token}
      const imagePathMatch = imageUrl.match(/\/o\/(.+?)\?/);
      if (imagePathMatch && imagePathMatch[1]) {
        const imagePath = decodeURIComponent(imagePathMatch[1]);
        const imageRef = ref(storage, imagePath);

        // Delete the image from Firebase Storage
        await deleteObject(imageRef);
        console.log('Image deleted from storage:', imagePath);
      }

      // Remove from state
      setFormData(prev => ({
        ...prev,
        images: prev.images.filter((_, i) => i !== index)
      }));

      toast.success('Image removed successfully');
    } catch (error) {
      console.error('Error deleting image:', error);
      // If deletion fails, still remove from state (might be already deleted)
      setFormData(prev => ({
        ...prev,
        images: prev.images.filter((_, i) => i !== index)
      }));
      toast.error('Failed to delete image from storage, but removed from product');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.title || !formData.price || !formData.category) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      setSaving(true);
      const productData = {
        ...formData,
        price: parseFloat(formData.price),
        discountedPrice: formData.discountedPrice ? parseFloat(formData.discountedPrice) : null,
        stock: parseInt(formData.stock) || 0,
        status: 'active',
        updatedAt: new Date()
      };

      if (editingProduct) {
        await updateDoc(doc(db, 'products', editingProduct.id), productData);
        toast.success('Product updated successfully');
      } else {
        productData.createdAt = new Date();
        await addDoc(collection(db, 'products'), productData);
        toast.success('Product added successfully');
      }

      resetForm();
      setShowModal(false);
      fetchProducts();
    } catch (error) {
      console.error('Error saving product:', error);
      toast.error('Failed to save product');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    setFormData({
      title: product.title || '',
      description: product.description || '',
      price: product.price || '',
      discountedPrice: product.discountedPrice || '',
      sku: product.sku || '',
      category: product.category || '',
      brand: product.brand || '',
      sizes: product.sizes || [],
      stock: product.stock || '',
      images: product.images || []
    });
    setShowModal(true);
  };

  const handleDelete = async (productId) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;

    try {
      // Find the product to get its images
      const product = products.find(p => p.id === productId);

      // Delete all product images from Firebase Storage
      if (product && product.images && product.images.length > 0) {
        const deletePromises = product.images.map(async (imageUrl) => {
          try {
            // Extract the file path from the Firebase Storage URL
            const imagePathMatch = imageUrl.match(/\/o\/(.+?)\?/);
            if (imagePathMatch && imagePathMatch[1]) {
              const imagePath = decodeURIComponent(imagePathMatch[1]);
              const imageRef = ref(storage, imagePath);
              await deleteObject(imageRef);
              console.log('Deleted image:', imagePath);
            }
          } catch (error) {
            console.error('Error deleting image:', imageUrl, error);
            // Continue with deletion even if some images fail
          }
        });

        // Wait for all image deletions to complete (or fail)
        await Promise.allSettled(deletePromises);
      }

      // Delete the product document from Firestore
      await deleteDoc(doc(db, 'products', productId));
      toast.success('Product and images deleted successfully');
      fetchProducts();
    } catch (error) {
      console.error('Error deleting product:', error);
      toast.error('Failed to delete product');
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      price: '',
      discountedPrice: '',
      sku: '',
      category: '',
      brand: '',
      sizes: [],
      stock: '',
      images: []
    });
    setEditingProduct(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-cream pt-20 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gold"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="pb-8 md:pb-16">
      <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6 md:mb-8">
          <div>
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-serif text-deep-brown">Products</h1>
            <p className="text-sm md:text-base text-warm-gray font-medium">Manage your product inventory</p>
          </div>
          <button
            onClick={() => setShowModal(true)}
            className="w-full sm:w-auto bg-gold text-deep-brown px-4 md:px-6 py-2 rounded-md hover:bg-yellow-500 transition duration-200 font-medium text-sm md:text-base"
          >
            Add New Product
          </button>
        </div>



        {/* Categories Management Section */}
        <div className="bg-white rounded-lg shadow-sm p-6 border border-warm-gray/20 mb-8">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
            <div>
              <h2 className="text-2xl font-serif text-deep-brown">Categories</h2>
              <p className="text-sm text-warm-gray mt-1">Manage product categories for each brand</p>
            </div>
            <button
              onClick={() => setShowCategoryModal(true)}
              className="w-full sm:w-auto bg-sage text-white px-4 py-2 rounded-md hover:bg-sage/80 transition duration-200 font-medium text-sm"
            >
              Add Category
            </button>
          </div>

          {/* Categories by Brand */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {brands.map(brand => (
              <div key={brand} className="border border-warm-gray/20 rounded-lg p-4 bg-cream">
                <h3 className="font-serif font-semibold text-deep-brown mb-3">{brand}</h3>
                <div className="flex flex-wrap gap-2">
                  {categories
                    .filter(cat => cat.brand === brand)
                    .length === 0 ? (
                    <p className="text-xs text-warm-gray italic">No categories yet</p>
                  ) : (
                    categories
                      .filter(cat => cat.brand === brand)
                      .map(cat => (
                        <div
                          key={cat.id}
                          className="flex items-center gap-2 bg-white px-3 py-1 rounded border border-warm-gray/30 group"
                        >
                          <span className="text-sm text-deep-brown">{cat.name}</span>
                          <button
                            onClick={() => handleDeleteCategory(cat.id, cat.name)}
                            disabled={deletingCategoryId === cat.id}
                            className="text-red-600 hover:text-red-700 opacity-0 group-hover:opacity-100 transition-all disabled:opacity-50"
                            title="Delete category"
                          >
                            {deletingCategoryId === cat.id ? (
                              <div className="animate-spin rounded-full h-3 w-3 border-b border-red-600"></div>
                            ) : (
                              '×'
                            )}
                          </button>
                        </div>
                      ))
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Products Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-6">
          {products.map((product) => (
            <div key={product.id} className="bg-white rounded-lg shadow-sm overflow-hidden border border-warm-gray/20">
              <div className="aspect-w-1 aspect-h-1 h-40 md:h-48 bg-cream">
                {product.images && product.images.length > 0 ? (
                  <img
                    src={product.images[0]}
                    alt={product.title}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="flex items-center justify-center h-full text-warm-gray text-sm">
                    No Image
                  </div>
                )}
              </div>
              <div className="p-3 md:p-4">
                <h3 className="font-serif font-semibold text-deep-brown mb-1 text-sm md:text-base truncate">{product.title}</h3>
                <p className="text-xs md:text-sm text-warm-gray mb-2">{product.brand}</p>
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <span className="text-base md:text-lg font-bold text-deep-brown">₹{product.price}</span>
                    {product.discountedPrice && (
                      <span className="text-xs md:text-sm text-warm-gray line-through ml-1 md:ml-2">
                        ₹{product.discountedPrice}
                      </span>
                    )}
                  </div>
                  <span className={`text-xs md:text-sm ${product.stock > 0 ? 'text-sage' : 'text-red-600'}`}>
                    Stock: {product.stock}
                  </span>
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => handleEdit(product)}
                    className="flex-1 bg-deep-brown text-cream px-2 md:px-3 py-1 rounded text-xs md:text-sm hover:bg-warm-gray transition duration-200"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDelete(product.id)}
                    className="flex-1 bg-red-600 text-white px-2 md:px-3 py-1 rounded text-xs md:text-sm hover:bg-red-700 transition duration-200"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {products.length === 0 && (
          <div className="text-center py-12">
            <p className="text-warm-gray font-medium">No products found. Add your first product!</p>
          </div>
        )}

        {/* Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-deep-brown bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border border-warm-gray/20 w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
              <LoadingOverlay show={uploading} message="Uploading images..." subMessage="Please wait, this may take a moment" progress={uploadProgress} />
              <LoadingOverlay show={saving} message={editingProduct ? 'Updating product...' : 'Creating product...'} />
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-serif font-semibold text-deep-brown">
                  {editingProduct ? 'Edit Product' : 'Add New Product'}
                </h3>
                <button
                  onClick={() => {
                    setShowModal(false);
                    resetForm();
                  }}
                  className="text-warm-gray hover:text-deep-brown"
                >
                  ✕
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4" aria-busy={uploading || saving}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-deep-brown mb-1">
                      Title *
                    </label>
                    <input
                      type="text"
                      name="title"
                      value={formData.title}
                      onChange={handleInputChange}
                      disabled={saving}
                      className="w-full px-3 py-2 border border-warm-gray/30 rounded-md focus:outline-none focus:ring-gold focus:border-gold disabled:opacity-60"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-deep-brown mb-1">
                      SKU
                    </label>
                    <input
                      type="text"
                      name="sku"
                      value={formData.sku}
                      onChange={handleInputChange}
                      disabled={saving}
                      className="w-full px-3 py-2 border border-warm-gray/30 rounded-md focus:outline-none focus:ring-gold focus:border-gold disabled:opacity-60"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-deep-brown mb-1">
                      Price *
                    </label>
                    <input
                      type="number"
                      name="price"
                      value={formData.price}
                      onChange={handleInputChange}
                      disabled={saving}
                      className="w-full px-3 py-2 border border-warm-gray/30 rounded-md focus:outline-none focus:ring-gold focus:border-gold disabled:opacity-60"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-deep-brown mb-1">
                      Discounted Price
                    </label>
                    <input
                      type="number"
                      name="discountedPrice"
                      value={formData.discountedPrice}
                      onChange={handleInputChange}
                      disabled={saving}
                      className="w-full px-3 py-2 border border-warm-gray/30 rounded-md focus:outline-none focus:ring-gold focus:border-gold disabled:opacity-60"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-deep-brown mb-1">
                      Category *
                    </label>
                    <select
                      name="category"
                      value={formData.category}
                      onChange={handleInputChange}
                      disabled={saving || !formData.brand}
                      className="w-full px-3 py-2 border border-warm-gray/30 rounded-md focus:outline-none focus:ring-gold focus:border-gold disabled:opacity-60"
                      required
                    >
                      <option value="">
                        {formData.brand ? 'Select Category' : 'Select Brand First'}
                      </option>
                      {categories
                        .filter(cat => cat.brand === formData.brand)
                        .map(cat => (
                          <option key={cat.id} value={cat.name}>{cat.name}</option>
                        ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-deep-brown mb-1">
                      Brand *
                    </label>
                    <select
                      name="brand"
                      value={formData.brand}
                      onChange={handleInputChange}
                      disabled={saving}
                      className="w-full px-3 py-2 border border-warm-gray/30 rounded-md focus:outline-none focus:ring-gold focus:border-gold disabled:opacity-60"
                      required
                    >
                      <option value="">Select Brand</option>
                      {brands.map(brand => (
                        <option key={brand} value={brand}>{brand}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-deep-brown mb-1">
                      Stock
                    </label>
                    <input
                      type="number"
                      name="stock"
                      value={formData.stock}
                      onChange={handleInputChange}
                      disabled={saving}
                      className="w-full px-3 py-2 border border-warm-gray/30 rounded-md focus:outline-none focus:ring-gold focus:border-gold disabled:opacity-60"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-deep-brown mb-1">
                    Description
                  </label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    rows="3"
                    disabled={saving}
                    className="w-full px-3 py-2 border border-warm-gray/30 rounded-md focus:outline-none focus:ring-gold focus:border-gold disabled:opacity-60"
                  ></textarea>
                </div>

                <div>
                  <label className="block text-sm font-medium text-deep-brown mb-1">
                    Sizes
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {availableSizes.map(size => (
                      <button
                        key={size}
                        type="button"
                        onClick={() => handleSizeToggle(size)}
                        className={`px-3 py-1 rounded border ${formData.sizes.includes(size)
                          ? 'bg-gold text-deep-brown border-gold'
                          : 'bg-white text-warm-gray border-warm-gray/30'
                          }`}
                      >
                        {size}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-deep-brown mb-1">
                    Images
                  </label>
                  <input
                    type="file"
                    multiple
                    accept="image/*"
                    onChange={handleImageUpload}
                    disabled={uploading || saving}
                    className="w-full px-3 py-2 border border-warm-gray/30 rounded-md focus:outline-none focus:ring-gold focus:border-gold disabled:opacity-60"
                  />
                  {uploading && (
                    <p className="text-sm text-gold mt-1">Uploading... {uploadProgress}%</p>
                  )}

                  {formData.images.length > 0 && (
                    <div className="mt-2 grid grid-cols-4 gap-2">
                      {formData.images.map((image, index) => (
                        <div key={index} className="relative">
                          <img
                            src={image}
                            alt={`Product ${index + 1}`}
                            className="w-full h-20 object-cover rounded border border-warm-gray/20"
                          />
                          <button
                            type="button"
                            onClick={() => removeImage(index)}
                            className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-5 h-5 text-xs"
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowModal(false);
                      resetForm();
                    }}
                    disabled={saving}
                    className="px-4 py-2 border border-warm-gray/30 rounded-md text-warm-gray hover:bg-cream disabled:opacity-60"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={saving || uploading}
                    className="px-4 py-2 bg-gold text-deep-brown rounded-md hover:bg-yellow-500 font-medium disabled:opacity-60"
                  >
                    {editingProduct ? 'Update Product' : 'Add Product'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Category Modal */}
        {showCategoryModal && (
          <div className="fixed inset-0 bg-deep-brown bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-lg shadow-lg max-w-md w-full p-6">
              <h2 className="text-2xl font-serif text-deep-brown mb-6">Add New Category</h2>

              <form onSubmit={handleAddCategory} className="space-y-4">
                {/* Brand Selection */}
                <div>
                  <label className="block text-sm font-medium text-deep-brown mb-2">
                    Brand *
                  </label>
                  <select
                    value={newCategory.brand}
                    onChange={(e) => setNewCategory(prev => ({ ...prev, brand: e.target.value }))}
                    className="w-full px-4 py-2 border border-warm-gray/30 rounded-md focus:outline-none focus:ring-2 focus:ring-gold focus:border-transparent text-deep-brown"
                  >
                    <option value="">Select a brand</option>
                    {brands.map(brand => (
                      <option key={brand} value={brand}>{brand}</option>
                    ))}
                  </select>
                </div>

                {/* Category Name */}
                <div>
                  <label className="block text-sm font-medium text-deep-brown mb-2">
                    Category Name *
                  </label>
                  <input
                    type="text"
                    value={newCategory.name}
                    onChange={(e) => setNewCategory(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="e.g., Sarees, Dresses, etc."
                    className="w-full px-4 py-2 border border-warm-gray/30 rounded-md focus:outline-none focus:ring-2 focus:ring-gold focus:border-transparent text-deep-brown"
                    disabled={savingCategory}
                  />
                </div>

                {/* Action Buttons */}
                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowCategoryModal(false);
                      setNewCategory({ name: '', brand: '' });
                    }}
                    disabled={savingCategory}
                    className="flex-1 px-4 py-2 border border-warm-gray/30 rounded-md text-deep-brown hover:bg-warm-gray/5 transition duration-200 disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={savingCategory}
                    className="flex-1 px-4 py-2 bg-gold text-deep-brown rounded-md hover:bg-yellow-500 transition duration-200 font-medium disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {savingCategory ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-deep-brown"></div>
                        <span>Adding...</span>
                      </>
                    ) : (
                      'Add Category'
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminProducts;